import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ReportValues from "../../../domains/ReportComparison/ReportValues";

import { SLICE_NAME } from "./types";

interface ComponentsReportTableState {
  componentsMissingValues: Array<string>
  formInitialValues: ReportValues
  isFormLoading: boolean
  hasErrorLoadingForm: boolean
  isFormSubmitting: boolean
  hasErrorSubmittingForm: boolean
  isAlertModalOpen: boolean
  isBrowseFilesModalOpen: boolean
  shouldShowPDFVisualizer: boolean
  currentReportId: string,
  shouldProceedWithoutRecommendation: boolean;
}

const initialState: ComponentsReportTableState = {
  componentsMissingValues: [],
  formInitialValues: {},
  isFormLoading: false,
  hasErrorLoadingForm: false,
  isFormSubmitting: false,
  hasErrorSubmittingForm: false,
  isAlertModalOpen: false,
  isBrowseFilesModalOpen: false,
  shouldShowPDFVisualizer: false,
  currentReportId: "",
  shouldProceedWithoutRecommendation: false,
};

export const componentsReportSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setComponentsMissingValues: (
      state: ComponentsReportTableState,
      action: PayloadAction<Array<string>>,
    ) => {
      state.componentsMissingValues = [...action.payload];
    },
    initializeForm: state => {
      state.isFormLoading = true;
    },
    setFormInitialValues: (
      state,
      action: PayloadAction<{ values: ReportValues }>,
    ) => {
      state.isFormLoading = false;
      state.formInitialValues = action.payload.values;
    },
    initializeFormError: state => {
      state.isFormLoading = false;
      state.hasErrorLoadingForm = true;
    },
    setCurrentReportId: (state, action: PayloadAction<string>) => {
      state.currentReportId = action.payload;
    },
    setValue: (
      state,
      action: PayloadAction<{ fieldName: string; value: string | number }>,
    ) => {
      const fieldName = action.payload.fieldName;
      const updatedValues = {
        ...state.formInitialValues,
        [fieldName]: {
          ...state.formInitialValues[fieldName],
          value: action.payload.value,
        },
      };
      state.formInitialValues = updatedValues as ReportValues;
    },

    updateEntry: (
      state,
      action: PayloadAction<{
        value?: number
        componentId?: string
        unit?: string
        hasWarning?: boolean
        warning?: string
        isOCRChecked?: boolean
      }>,
    ) => {
      state.formInitialValues[action?.payload?.componentId as string] = {
        ...state.formInitialValues[action?.payload?.componentId as string],
        unit: action?.payload?.unit as string,
        value: String(action.payload.value),
        OCRWarning: {
          warning: action?.payload?.warning as string,
          hasWarning: action?.payload?.hasWarning as boolean,
        },
        isOCRChecked: action.payload.isOCRChecked,
      };
    },
    removeOCRWarning: (state, action: PayloadAction<string>) => {
      state.formInitialValues[action.payload] = {
        ...state.formInitialValues[action.payload],
        OCRWarning: {
          hasWarning: false,
          warning: "",
        },
        isOCRChecked: true,
      };
    },
    submitForm: state => {
      state.isFormSubmitting = true;
    },
    submitFormSuccess: state => {
      state.isFormSubmitting = false;
      state.hasErrorSubmittingForm = false;
    },
    submitFormError: state => {
      state.isFormSubmitting = false;
      state.hasErrorSubmittingForm = true;
    },
    openAlertModal: state => {
      state.isAlertModalOpen = true;
    },
    closeAlertModal: state => {
      state.isAlertModalOpen = false;
    },
    clearForm: state => {
      state.formInitialValues = {};
    },
    openBrowseFilesModal: state => {
      state.isBrowseFilesModalOpen = true;
    },
    closeBrowseFilesModal: state => {
      state.isBrowseFilesModalOpen = false;
    },
    togglePDFVisualizer: (state, action: PayloadAction<boolean>) => {
      state.shouldShowPDFVisualizer = action.payload;
    },
    shouldProceedWithoutRecommendation: (state, action: PayloadAction<boolean>) => {
      state.shouldProceedWithoutRecommendation = action.payload;
    }
  },
});

export const {
  setComponentsMissingValues,
  setFormInitialValues,
  setValue,
  initializeFormError,
  initializeForm,
  submitFormError,
  submitForm,
  submitFormSuccess,
  openAlertModal,
  closeAlertModal,
  clearForm,
  openBrowseFilesModal,
  closeBrowseFilesModal,
  togglePDFVisualizer,
  setCurrentReportId,
  updateEntry,
  removeOCRWarning,
  shouldProceedWithoutRecommendation,
} = componentsReportSlice.actions;

export default componentsReportSlice.reducer;
